import React, { FC, useEffect, useRef, useState } from 'react';
import { alpha, styled } from '@mui/material/styles';
import { Box } from '@mui/material';
import { BODY_FONT, g, t, TICKET } from '../../HomeScreen/HomeScreen.styles';
import { CAV6 } from '../../cav6tokens';
import { useTypedSelector } from '../../../../store/store';
import { httpClient } from '../../../../services/httpClient/httpClient';
import { AccountEndpoints, getApiUrlForId } from '../../../../api/endpoints';
import { useTrackEvent } from '../../../../common/hooks/useTrackEvent';
import { EventNames } from '../../../../common/constants/constants';
import { getAttempt } from '../../../../services/analytics/telemetry';
import {
  getLocationPermissionState,
  hasDeviceLocation,
  requestDeviceLocation,
} from '../../../../services/analytics/deviceLocation';

/* Step after identity: shows the business and its address, asks whether the
   video is about it, and on either answer asks for the device location. The
   request runs inside the tap, so the browser prompt follows the hint the
   step shows. The upload keeps going underneath. */

const Title = styled('h2')({
  fontFamily: CAV6.font,
  fontStyle: 'italic',
  fontWeight: 800,
  fontSize: t(34),
  lineHeight: 1.18,
  letterSpacing: '-0.02em',
  color: TICKET.labelInk,
  margin: 0,
  textAlign: 'center',
});

const Card = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: g(22),
  marginTop: g(28),
  padding: `${g(24)} ${g(26)}`,
  borderRadius: g(16),
  border: '1.5px solid #e4e2dd',
  background: '#fff',
  textAlign: 'left',
});

const PinCircle = styled('span')(({ theme }) => ({
  flex: '0 0 auto',
  display: 'grid',
  placeItems: 'center',
  width: `max(44px, ${g(66)})`,
  height: `max(44px, ${g(66)})`,
  borderRadius: '50%',
  background: alpha(theme.palette.primary.main, 0.12),
  color: theme.palette.primary.main,
}));

const BusinessName = styled('p')({
  margin: 0,
  fontFamily: BODY_FONT,
  fontWeight: 700,
  fontSize: t(21),
  lineHeight: 1.25,
  color: TICKET.labelInk,
});

const AddressLine = styled('p')({
  margin: `${g(4)} 0 0`,
  fontFamily: BODY_FONT,
  fontSize: t(16.5),
  lineHeight: 1.35,
  color: TICKET.tagline,
});

const Question = styled('p')({
  margin: `${g(30)} 0 0`,
  fontFamily: BODY_FONT,
  fontWeight: 700,
  fontSize: t(21),
  lineHeight: 1.3,
  color: TICKET.labelInk,
  textAlign: 'center',
});

const ButtonRow = styled(Box)({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: g(14),
  marginTop: g(22),
});

const AnswerButton = styled('button', { shouldForwardProp: (p) => p !== '$primary' })<{ $primary?: boolean }>(
  ({ theme, $primary }) => ({
    height: `max(48px, ${g(66)})`,
    borderRadius: g(12),
    border: $primary ? 'none' : `2px solid ${TICKET.labelInk}`,
    background: $primary ? theme.palette.primary.main : 'transparent',
    color: $primary ? theme.palette.primary.contrastText : TICKET.labelInk,
    fontFamily: BODY_FONT,
    fontWeight: 700,
    fontSize: t(21),
    boxShadow: $primary ? '0 1.5px 2px rgba(0,0,0,.18), 0 6px 14px rgba(0,0,0,.08)' : 'none',
    cursor: 'pointer',
    userSelect: 'none',
    WebkitTapHighlightColor: 'transparent',
    transition: 'transform 160ms ease, opacity 160ms ease',
    '&:active': { transform: 'scale(0.985)' },
    '&:focus-visible': { outline: `3px solid ${theme.palette.primary.main}`, outlineOffset: '2px' },
    '&:disabled': { opacity: 0.55, cursor: 'default', transform: 'none' },
  }),
);

const Hint = styled('p')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: g(8),
  margin: `${g(16)} 0 0`,
  fontFamily: BODY_FONT,
  fontSize: t(15),
  color: TICKET.statText,
});

const PinIcon: FC<{ size: string; strokeWidth?: number }> = ({ size, strokeWidth = 2 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M12 21s-6.5-5.6-6.5-11a6.5 6.5 0 0 1 13 0c0 5.4-6.5 11-6.5 11Z"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinejoin="round"
    />
    <circle cx="12" cy="10" r="2.4" stroke="currentColor" strokeWidth={strokeWidth} />
  </svg>
);

interface Address {
  address: string;
  city: string;
  state: string;
  zip: string;
}

/** The account's address lines; fetched here rather than through the account
    slice, whose address thunk also flips the page's loading/error flags. */
const useBusinessAddress = (accountId: string, fallback: string): string[] | null => {
  // null until the lookup settles.
  const [lines, setLines] = useState<string[] | null>(null);
  useEffect(() => {
    if (!accountId) return undefined;
    let cancelled = false;
    httpClient
      .get<undefined, Address>({
        url: getApiUrlForId(AccountEndpoints.GetAccountAddress, accountId),
        requiresToken: false,
      })
      .then((a) => {
        const cityLine = [a?.city, [a?.state, a?.zip].filter(Boolean).join(' ')].filter(Boolean).join(', ');
        const next = [a?.address, cityLine].filter(Boolean) as string[];
        if (!cancelled) setLines(next.length ? next : fallback ? [fallback] : []);
      })
      .catch(() => {
        if (!cancelled) setLines(fallback ? [fallback] : []);
      });
    return () => {
      cancelled = true;
    };
  }, [accountId, fallback]);
  return lines;
};

export type BusinessAnswer = 'yes' | 'no';

interface Props {
  isActive: boolean;
  onAnswer: (answer: BusinessAnswer) => void;
}

export const BusinessConfirmBlock: FC<Props> = ({ isActive, onAnswer }) => {
  const { trackEvent } = useTrackEvent();
  const { id: accountId, name, businessAddress } = useTypedSelector((state) => state.account.account);
  const addressLines = useBusinessAddress(accountId, businessAddress);
  const addressState = addressLines === null ? 'loading' : addressLines.length ? 'shown' : 'none';
  const [permission, setPermission] = useState<PermissionState | 'unknown' | null>(null);
  const [answered, setAnswered] = useState(false);
  const shownAtRef = useRef<number | null>(null);

  useEffect(() => {
    if (!isActive) return;
    let cancelled = false;
    getLocationPermissionState().then((state) => {
      if (cancelled) return;
      setPermission(state);
      if (!getAttempt()) return;
      shownAtRef.current = performance.now();
      trackEvent({
        action: EventNames.business_confirm_view,
        address_state: addressState,
        permission: state,
        hint_shown: !hasDeviceLocation() && state !== 'denied' && state !== 'granted',
      });
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive]);

  // No location yet and the site isn't blocked: the tap asks for it.
  const willRequest = !hasDeviceLocation() && permission !== null && permission !== 'denied';
  // Only promise a prompt when the browser will actually show one.
  const showHint = willRequest && permission !== 'granted';

  const handleAnswer = (answer: BusinessAnswer) => {
    if (answered) return;
    setAnswered(true);
    trackEvent({
      action: EventNames.business_confirm_answer,
      answer,
      ms_since_view: shownAtRef.current ? Math.round(performance.now() - shownAtRef.current) : null,
      // What the card showed when they answered (it may have loaded after the view).
      has_address: !!addressLines?.length,
      location_requested: willRequest,
    });
    if (willRequest) {
      const before = permission;
      requestDeviceLocation('business_confirm').then((result) =>
        trackEvent({ action: EventNames.device_location_result, ...result, permission_before: before }),
      );
    }
    setTimeout(() => onAnswer(answer), 400);
  };

  return (
    <section aria-label="Confirm the business">
      <Title>Thank you for sharing your story!</Title>
      <Card>
        <PinCircle>
          <PinIcon size={`max(22px, ${g(32)})`} />
        </PinCircle>
        <Box>
          <BusinessName>{name}</BusinessName>
          {addressLines?.map((line) => (
            <AddressLine key={line}>{line}</AddressLine>
          ))}
        </Box>
      </Card>
      <Question>Confirm your video is about this business at this location?</Question>
      <ButtonRow>
        <AnswerButton type="button" $primary onClick={() => handleAnswer('yes')} disabled={answered}>
          Yes
        </AnswerButton>
        <AnswerButton type="button" onClick={() => handleAnswer('no')} disabled={answered}>
          No
        </AnswerButton>
      </ButtonRow>
      {showHint && (
        <Hint>
          <PinIcon size="16px" strokeWidth={1.8} />
          Next, we&apos;ll ask to confirm your location.
        </Hint>
      )}
    </section>
  );
};
